import React from "react";
import type { FC, ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { Hospital } from "@shared/models";
import { useDispatch, useSelector } from "react-redux";
import { checkPermissions, getAllowedHospitalIds, getDefaultUrl, isAppAdmin } from "@shared/utils";
import { getUserDetails } from "@shared/store/selectors";
import { actions } from "@containers/Hospital/store";

import { authentificated } from "../../containers/Auth/store/selectors";
import { NamesOfParentRoutes } from "../constants";

interface AuthGuardProps {
  children?: ReactNode;
  permissions?: string[];
  hospital?: Hospital | null;
  isInit?: boolean;
}

const AuthGuard: FC<AuthGuardProps> = (props) => {
  const { children, permissions, hospital, isInit } = props;
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(authentificated());
  const authenticatedUser = useSelector(getUserDetails());
  const allowedHospitalIds = getAllowedHospitalIds(authenticatedUser);

  if (authenticatedUser && isAppAdmin(authenticatedUser)) {
    return <>{children}</>;
  }

  if (hospital && !allowedHospitalIds.find((id) => id === hospital.id)) {
    dispatch(actions.getHospital.success(null));
    return <Navigate to={authenticatedUser ? getDefaultUrl(authenticatedUser) : NamesOfParentRoutes.AUTH} />;
  }

  const hasPermission = Array.isArray(permissions) ? checkPermissions(permissions) : isAuthenticated;
  if (!hasPermission) {
    return <Navigate to={authenticatedUser ? getDefaultUrl(authenticatedUser) : NamesOfParentRoutes.AUTH} />;
  }

  if (isInit && authenticatedUser) {
    return <Navigate to={getDefaultUrl(authenticatedUser)} />;
  }

  if (!children) {
    return <Navigate to={NamesOfParentRoutes.AUTH} />;
  }

  return <>{children}</>;
};

export default AuthGuard;
